import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  FormControlLabel,
  Switch,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ListSubheader,
} from '@mui/material';
import { createAccessory } from '../../../../firebase/services/accessoryService';
import { 
  getAccessoryTypesByCategory, 
  addAccessoryTypeIfNotExists
} from '../../../../firebase/services/accessoryTypeService';
import {
  getBrands,
  getModels,
  getSizes,
  getColors,
  getLocations,
  getMaintenanceIntervals,
  addBrandIfNotExists,
  addModelIfNotExists,
  addSizeIfNotExists,
  addColorIfNotExists,
  addLocationIfNotExists,
  addMaintenanceIntervalIfNotExists
} from '../../../../firebase/services/accessoryAttributeService';
import { Accessory } from '../../../../models/types';

// Custom SearchableSelect Component
interface SearchableSelectProps {
  value: string;
  options: string[];
  label: string;
  placeholder?: string;
  loading?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  onChange: (value: string) => void;
  onAddNew?: (newValue: string) => void;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  value,
  options,
  label,
  placeholder,
  loading = false,
  required = false,
  fullWidth = true,
  onChange,
  onAddNew
}) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newItemText, setNewItemText] = useState('');

  // Update input value when prop value changes
  React.useEffect(() => {
    setInputValue(value);
  }, [value]);

  const filteredOptions = options.filter(option =>
    option.toLowerCase().includes(inputValue.toLowerCase())
  );

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setInputValue(newValue);
    setOpen(true);
    
    // If the typed value exactly matches an existing option, update the parent
    if (options.includes(newValue)) {
      onChange(newValue);
    }
  };

  const handleOptionSelect = (selectedValue: string) => {
    if (selectedValue === '__ADD_NEW__') {
      setNewItemText(inputValue);
      setAddDialogOpen(true);
    } else {
      setInputValue(selectedValue);
      onChange(selectedValue);
    }
    setOpen(false);
  };

  const handleInputFocus = () => {
    setOpen(true);
  };

  const handleInputBlur = () => {
    // Delay closing to allow option selection
    setTimeout(() => {
      setOpen(false);
      // If input value doesn't match any option and it's not empty, ask to add it
      if (inputValue && !options.includes(inputValue) && onAddNew) {
        // Only suggest adding if the user typed something meaningful
        if (inputValue.trim().length > 0) {
          onChange(inputValue); // Set the value anyway
        }
      }
    }, 150);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (filteredOptions.length > 0) {
        // Select the first filtered option
        const firstOption = filteredOptions[0];
        setInputValue(firstOption);
        onChange(firstOption);
        setOpen(false);
      } else if (inputValue.trim() && onAddNew) {
        // If no options match and we can add new items, add it
        setNewItemText(inputValue);
        setAddDialogOpen(true);
      }
    } else if (event.key === 'Escape') {
      setOpen(false);
      setInputValue(value); // Reset to original value
    }
  };

  const handleAddNew = () => {
    if (newItemText.trim() && onAddNew) {
      onAddNew(newItemText.trim());
      onChange(newItemText.trim());
      setInputValue(newItemText.trim());
    }
    setAddDialogOpen(false);
    setNewItemText('');
  };

  return (
    <>
      <Box sx={{ position: 'relative', width: fullWidth ? '100%' : 'auto' }}>
        <TextField
          fullWidth={fullWidth}
          label={label}
          required={required}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder || `Search or select ${label.toLowerCase()}...`}
          disabled={loading}
          InputProps={{
            endAdornment: loading ? <CircularProgress size={20} /> : null,
          }}
        />
        
        {open && (filteredOptions.length > 0 || (inputValue && !options.includes(inputValue))) && (
          <Paper
            sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              zIndex: 1300,
              maxHeight: 300,
              overflow: 'auto',
              mt: 0.5,
            }}
          >
            {loading ? (
              <MenuItem disabled>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Loading...
              </MenuItem>
            ) : (
              <>
                {filteredOptions.map((option) => (
                  <MenuItem 
                    key={option} 
                    onClick={() => handleOptionSelect(option)}
                    selected={option === value}
                  >
                    {option}
                  </MenuItem>
                ))}
                {inputValue && 
                 !options.includes(inputValue) && 
                 inputValue.trim().length > 0 && 
                 onAddNew && (
                  <MenuItem 
                    onClick={() => handleOptionSelect('__ADD_NEW__')}
                    sx={{ 
                      fontStyle: 'italic',
                      color: 'primary.main',
                      borderTop: filteredOptions.length > 0 ? '1px solid #e0e0e0' : 'none'
                    }}
                  >
                    Add "{inputValue}"
                  </MenuItem>
                )}
              </>
            )}
          </Paper>
        )}
      </Box>

      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)}>
        <DialogTitle>Add New {label}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={`New ${label}`}
            fullWidth
            variant="outlined"
            value={newItemText}
            onChange={(e) => setNewItemText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddNew} variant="contained">Add</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const AddAccessoryPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [typesLoading, setTypesLoading] = useState(false);
  const [availableTypes, setAvailableTypes] = useState<string[]>([]);
  
  // Attribute options and loading states
  const [availableBrands, setAvailableBrands] = useState<string[]>([]);
  const [brandsLoading, setBrandsLoading] = useState(false);
  const [availableModels, setAvailableModels] = useState<string[]>([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [availableSizes, setAvailableSizes] = useState<string[]>([]);
  const [sizesLoading, setSizesLoading] = useState(false);
  const [availableColors, setAvailableColors] = useState<string[]>([]);
  const [colorsLoading, setColorsLoading] = useState(false);
  const [availableLocations, setAvailableLocations] = useState<string[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [availableIntervals, setAvailableIntervals] = useState<string[]>([]);
  const [intervalsLoading, setIntervalsLoading] = useState(false);
  
  const [accessory, setAccessory] = useState<Omit<Accessory, 'id'>>({
    name: '',
    category: 'safety',
    type: '',
    brand: '',
    model: '',
    size: '',
    color: '',
    quantity: 1,
    availableQuantity: 1,
    reservedQuantity: 0,
    condition: 'excellent',
    status: 'active',
    location: '',
    cost: 0,
    replacementCost: 0,
    serialNumber: '',
    barcode: '',
    rentalRate: 0,
    description: '',
    notes: '',
    maintenanceInterval: 0,
    isRentable: false,
    requiresDeposit: false,
    depositAmount: 0,
    images: [],
    createdAt: new Date(),
    updatedAt: new Date(),
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setAccessory((prev) => ({
        ...prev,
        [name]: checked,
      }));
    } else if (type === 'number') {
      setAccessory((prev) => ({
        ...prev,
        [name]: value === '' ? 0 : Number(value),
      }));
    } else {
      setAccessory((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setAccessory((prev) => {
      const updated = {
        ...prev,
        [name]: value,
      };
      
      // Clear type when category changes to force user to select appropriate type
      if (name === 'category') {
        updated.type = '';
        // Load types for the new category
        loadTypesForCategory(value);
      }
      
      return updated;
    });
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setAccessory((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const numValue = value === '' ? 0 : Number(value);
    
    setAccessory((prev) => {
      const updated = {
        ...prev,
        [name]: numValue,
      };
      
      // Auto-adjust availableQuantity when quantity changes
      if (name === 'quantity') {
        updated.availableQuantity = numValue - (prev.reservedQuantity || 0);
      }
      
      return updated;
    });
  };

  // Handlers for SearchableSelect components
  const handleTypeChange = async (newType: string) => {
    setAccessory((prev) => ({
      ...prev,
      type: newType,
      name: newType, // Auto-set name to match the type
    }));
  };

  const handleAddNewType = async (newType: string) => {
    try {
      await addAccessoryTypeIfNotExists(accessory.category, newType);
      await loadTypesForCategory(accessory.category);
    } catch (error) {
      console.error('Error adding new accessory type:', error);
    }
  };

  const handleBrandChange = async (newBrand: string) => {
    setAccessory((prev) => ({
      ...prev,
      brand: newBrand,
    }));
  };

  const handleAddNewBrand = async (newBrand: string) => {
    try {
      await addBrandIfNotExists(newBrand);
      await loadBrands();
    } catch (error) {
      console.error('Error adding new brand:', error);
    }
  };

  const handleModelChange = async (newModel: string) => {
    setAccessory((prev) => ({
      ...prev,
      model: newModel,
    }));
  };

  const handleAddNewModel = async (newModel: string) => {
    try {
      await addModelIfNotExists(newModel);
      await loadModels();
    } catch (error) {
      console.error('Error adding new model:', error);
    }
  };

  const handleSizeChange = async (newSize: string) => {
    setAccessory((prev) => ({
      ...prev,
      size: newSize,
    }));
  };

  const handleAddNewSize = async (newSize: string) => {
    try {
      await addSizeIfNotExists(newSize);
      await loadSizes();
    } catch (error) {
      console.error('Error adding new size:', error);
    }
  };

  const handleColorChange = async (newColor: string) => {
    setAccessory((prev) => ({
      ...prev,
      color: newColor,
    }));
  };

  const handleAddNewColor = async (newColor: string) => {
    try {
      await addColorIfNotExists(newColor);
      await loadColors();
    } catch (error) {
      console.error('Error adding new color:', error);
    }
  };

  const handleLocationChange = async (newLocation: string) => {
    setAccessory((prev) => ({
      ...prev,
      location: newLocation,
    }));
  };

  const handleAddNewLocation = async (newLocation: string) => {
    try {
      await addLocationIfNotExists(newLocation);
      await loadLocations();
    } catch (error) {
      console.error('Error adding new location:', error);
    }
  };

  const handleIntervalChange = async (newInterval: string) => {
    setAccessory((prev) => ({
      ...prev,
      maintenanceInterval: newInterval ? Number(newInterval) : 0,
    }));
  };

  const handleAddNewInterval = async (newInterval: string) => {
    try {
      await addMaintenanceIntervalIfNotExists(newInterval);
      await loadIntervals();
    } catch (error) {
      console.error('Error adding new maintenance interval:', error);
    }
  };

  // Generate barcode from type
  const generateBarcode = () => {
    if (!accessory.type) return;
    
    // Get first letter of each word in the type
    const words = accessory.type.trim().split(/\s+/);
    const letters = words
      .map(word => word.charAt(0).toUpperCase())
      .join('')
      .replace(/[^A-Z0-9]/g, ''); // Strip any special characters, keep only letters and numbers
    
    // Generate 4 random digits
    const randomDigits = Math.floor(1000 + Math.random() * 9000); // Ensures 4 digits
    
    // Combine letters and digits
    const barcode = `${letters}${randomDigits}`;
    
    setAccessory((prev) => ({
      ...prev,
      barcode: barcode,
    }));
  };

  // Load types for a specific category
  const loadTypesForCategory = async (category: string) => {
    if (!category) {
      setAvailableTypes([]);
      return;
    }

    console.log('Loading types for category:', category);
    setTypesLoading(true);
    try {
      const types = await getAccessoryTypesByCategory(category);
      const typeStrings = types.map(t => t.type);
      console.log('Loaded types:', typeStrings);
      setAvailableTypes(typeStrings);
    } catch (error) {
      console.error('Error loading accessory types:', error);
      setAvailableTypes([]);
    } finally {
      setTypesLoading(false);
    }
  };

  // Load attribute options
  const loadBrands = async () => {
    setBrandsLoading(true);
    try {
      const brands = await getBrands();
      setAvailableBrands(brands.map(b => b.value));
    } catch (error) {
      console.error('Error loading brands:', error);
      setAvailableBrands([]);
    } finally {
      setBrandsLoading(false);
    }
  };

  const loadModels = async () => {
    setModelsLoading(true);
    try {
      const models = await getModels();
      setAvailableModels(models.map(m => m.value));
    } catch (error) {
      console.error('Error loading models:', error);
      setAvailableModels([]);
    } finally {
      setModelsLoading(false);
    }
  };

  const loadSizes = async () => {
    setSizesLoading(true);
    try {
      const sizes = await getSizes();
      setAvailableSizes(sizes.map(s => s.value));
    } catch (error) {
      console.error('Error loading sizes:', error);
      setAvailableSizes([]);
    } finally {
      setSizesLoading(false);
    }
  };

  const loadColors = async () => {
    setColorsLoading(true);
    try {
      const colors = await getColors();
      setAvailableColors(colors.map(c => c.value));
    } catch (error) {
      console.error('Error loading colors:', error);
      setAvailableColors([]);
    } finally {
      setColorsLoading(false);
    }
  };

  const loadLocations = async () => {
    setLocationsLoading(true);
    try {
      const locations = await getLocations();
      setAvailableLocations(locations.map(l => l.value));
    } catch (error) {
      console.error('Error loading locations:', error);
      setAvailableLocations([]);
    } finally {
      setLocationsLoading(false);
    }
  };

  const loadIntervals = async () => {
    setIntervalsLoading(true);
    try {
      const intervals = await getMaintenanceIntervals();
      setAvailableIntervals(intervals.map(i => i.value));
    } catch (error) {
      console.error('Error loading maintenance intervals:', error);
      setAvailableIntervals([]);
    } finally {
      setIntervalsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Auto-generate name from type if not provided
      const accessoryToCreate = {
        ...accessory,
        name: accessory.name || accessory.type || 'Unnamed Accessory'
      };
      await createAccessory(accessoryToCreate);
      navigate('/admin/equipment/accessories');
    } catch (error) {
      console.error('Error creating accessory:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load existing attribute options on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        // Only load existing options, don't create defaults
        await Promise.all([
          loadTypesForCategory(accessory.category),
          loadBrands(),
          loadModels(),
          loadSizes(),
          loadColors(),
          loadLocations(),
          loadIntervals()
        ]);
      } catch (error) {
        console.error('Error loading accessory data:', error);
      }
    };

    loadData();
  }, []);

  // Re-load types when category changes
  useEffect(() => {
    if (accessory.category) {
      loadTypesForCategory(accessory.category);
    }
  }, [accessory.category]);

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Add New Accessory
        </Typography>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Basic Information
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Category</InputLabel>
                <Select
                  name="category"
                  value={accessory.category}
                  onChange={handleSelectChange}
                >
                  <MenuItem value="safety">Safety</MenuItem>
                  <MenuItem value="navigation">Navigation</MenuItem>
                  <MenuItem value="fishing">Fishing</MenuItem>
                  <MenuItem value="recreation">Recreation</MenuItem>
                  <MenuItem value="maintenance">Maintenance</MenuItem>
                  <MenuItem value="comfort">Comfort</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <SearchableSelect
                value={accessory.type}
                options={availableTypes}
                label="Type"
                loading={typesLoading}
                required
                onChange={handleTypeChange}
                onAddNew={handleAddNewType}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Condition</InputLabel>
                <Select
                  name="condition"
                  value={accessory.condition}
                  onChange={handleSelectChange}
                >
                  <MenuItem value="excellent">Excellent</MenuItem>
                  <MenuItem value="good">Good</MenuItem>
                  <MenuItem value="fair">Fair</MenuItem>
                  <MenuItem value="poor">Poor</MenuItem>
                  <MenuItem value="out-of-service">Out of Service</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={accessory.status}
                  onChange={handleSelectChange}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="maintenance">Maintenance</MenuItem>
                  <MenuItem value="lost">Lost</MenuItem>
                  <MenuItem value="retired">Retired</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {/* Physical Details */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2, mt: 2 }}>
                Physical Details
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <SearchableSelect
                value={accessory.brand}
                options={availableBrands}
                label="Brand"
                loading={brandsLoading}
                onChange={handleBrandChange}
                onAddNew={handleAddNewBrand}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <SearchableSelect
                value={accessory.model}
                options={availableModels}
                label="Model"
                loading={modelsLoading}
                onChange={handleModelChange}
                onAddNew={handleAddNewModel}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <SearchableSelect
                value={accessory.size}
                options={availableSizes}
                label="Size"
                loading={sizesLoading}
                onChange={handleSizeChange}
                onAddNew={handleAddNewSize}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <SearchableSelect
                value={accessory.color}
                options={availableColors}
                label="Color"
                loading={colorsLoading}
                onChange={handleColorChange}
                onAddNew={handleAddNewColor}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Serial Number"
                name="serialNumber"
                value={accessory.serialNumber}
                onChange={handleChange}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Barcode"
                name="barcode"
                value={accessory.barcode}
                onChange={handleChange}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={generateBarcode}
                        disabled={!accessory.type}
                        sx={{ minWidth: 'auto', px: 1 }}
                      >
                        Generate
                      </Button>
                    </InputAdornment>
                  ),
                }}
                helperText={!accessory.type ? "Select a type first to generate barcode" : "Click Generate to create barcode from type"}
              />
            </Grid>
            
            {/* Inventory Details */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2, mt: 2 }}>
                Inventory & Quantities
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Total Quantity"
                name="quantity"
                type="number"
                value={accessory.quantity === 0 ? '' : accessory.quantity}
                onChange={handleQuantityChange}
                required
                inputProps={{ min: 1 }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Available Quantity"
                name="availableQuantity"
                type="number"
                value={accessory.availableQuantity === 0 ? '' : accessory.availableQuantity}
                onChange={handleChange}
                required
                inputProps={{ min: 0, max: accessory.quantity }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <SearchableSelect
                value={accessory.location}
                options={availableLocations}
                label="Storage Location"
                loading={locationsLoading}
                onChange={handleLocationChange}
                onAddNew={handleAddNewLocation}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <SearchableSelect
                value={accessory.maintenanceInterval === 0 ? '' : accessory.maintenanceInterval.toString()}
                options={availableIntervals}
                label="Maintenance Interval (days)"
                loading={intervalsLoading}
                onChange={handleIntervalChange}
                onAddNew={handleAddNewInterval}
              />
            </Grid>
            
            {/* Financial Information */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2, mt: 2 }}>
                Financial Information
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Purchase Cost"
                name="cost"
                type="number"
                value={accessory.cost === 0 ? '' : accessory.cost}
                onChange={handleChange}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Replacement Cost"
                name="replacementCost"
                type="number"
                value={accessory.replacementCost === 0 ? '' : accessory.replacementCost}
                onChange={handleChange}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>
            
            {/* Rental Settings */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2, mt: 2 }}>
                Rental Settings
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={accessory.isRentable}
                    onChange={handleSwitchChange}
                    name="isRentable"
                  />
                }
                label="Available for Rental"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={accessory.requiresDeposit}
                    onChange={handleSwitchChange}
                    name="requiresDeposit"
                    disabled={!accessory.isRentable}
                  />
                }
                label="Requires Deposit"
              />
            </Grid>
            
            {accessory.isRentable && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Rental Rate (per day)"
                    name="rentalRate"
                    type="number"
                    value={accessory.rentalRate === 0 ? '' : accessory.rentalRate}
                    onChange={handleChange}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </Grid>
                
                {accessory.requiresDeposit && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Deposit Amount"
                      name="depositAmount"
                      type="number"
                      value={accessory.depositAmount === 0 ? '' : accessory.depositAmount}
                      onChange={handleChange}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                      inputProps={{ min: 0, step: 0.01 }}
                    />
                  </Grid>
                )}
              </>
            )}
            
            {/* Description & Notes */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2, mt: 2 }}>
                Additional Information
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                multiline
                rows={3}
                value={accessory.description}
                onChange={handleChange}
                placeholder="Detailed description of the accessory..."
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                name="notes"
                multiline
                rows={2}
                value={accessory.notes}
                onChange={handleChange}
                placeholder="Internal notes, special handling instructions, etc."
              />
            </Grid>
          </Grid>
          
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="button"
              onClick={() => navigate('/admin/equipment/accessories')}
              sx={{ mr: 2 }}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Create Accessory'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default AddAccessoryPage; 